customElements.whenDefined('search-form').then(() => {
  class PredictiveSearch extends SearchForm {
    constructor() {
      super();
      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
      this.isOpen = false;
      this.abortController = new AbortController();
      this.searchTerm = '';
      this.productTypes = this.querySelector('.js-product-types');
      window.initScriptOnDemand(this, this.init.bind(this));
    }

    init() {
      this.setupEventListeners();
    }

    setupEventListeners() {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.input.addEventListener('input', debounce((event) => { this.onChange(event);}, 300).bind(this));
      this.productTypes?.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      if (this.closest('.template-search__search') || this.closest('.header__search-bar')) {
        this.addEventListener('focusout', this.onFocusOut.bind(this));
      }

      this.addEventListener('click', (event) => {
        const chip = event.target.closest('[data-phi-query]');
        if (chip) {
          this.input.value = chip.dataset.phiQuery;
          this.input.focus();
          this.onChange();
          return;
        }
        if (event.target.matches('.inline-search__close-button') || event.target.closest('.inline-search__close-button')) {
          this.onFormReset();
          this.close();
          document.activeElement.blur();
          setTimeout(() => { document.activeElement.blur(); }, 500);
        }
      });
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const newSearchTerm = this.getQuery();
      this.abortController.abort();
      this.requestId = (this.requestId || 0) + 1;
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        this.querySelector('#predictive-search-results-groups-wrapper')?.classList.add('opacity-20');
      }

      if(newSearchTerm.length === 0){
        // Remove the results when they are no longer relevant for the new search term
        // so they don't show up when the dropdown opens again
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      }

      this.searchTerm = newSearchTerm;

      if (!this.searchTerm.length) {
        this.close(true);
        this.showDiscovery();
        return;
      }

      this.getSearchResults(this.searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    }

    onFormReset(event) {
      super.onFormReset(event);
      if (super.shouldResetForm()) {
        this.searchTerm = '';
        this.abortController.abort();
        this.abortController = new AbortController();
        this.closeResults(true);
      }
    }

    onFocus() {
      const currentSearchTerm = this.getQuery();

      if (window.visualViewport) {
        setTimeout(() => {
          document.documentElement.style.setProperty('--bvh', `${window.visualViewport.height}px`);
        }, 100);

        setTimeout(() => {
          document.documentElement.style.setProperty('--bvh', `${window.visualViewport.height}px`);
        }, 500);
      }

      if (!currentSearchTerm.length) {
        this.showDiscovery();
        return;
      }

      if (this.searchTerm !== currentSearchTerm) {
        // Search term was changed from other search input, treat it as a user change
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) {
          this.close();
        }
      });
    }

    onKeyup(event) {
      if (!this.getQuery().length) this.close(true);
      if (!['ArrowUp', 'ArrowDown', 'Enter', 'Escape'].includes(event.code)) return;
      event.preventDefault();

      switch (event.code) {
        case 'Escape':
          this.close();
          this.input.focus();
          break;
        case 'ArrowUp':
          this.switchOption('up');
          break;
        case 'ArrowDown':
          this.switchOption('down');
          break;
        case 'Enter':
          this.selectOption();
          break;
      }
    }

    onKeydown(event) {
      // Prevent the cursor from moving in the input when using the up and down arrow keys
      if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
        event.preventDefault();
      }
    }

    switchOption(direction) {
      if (!this.getAttribute('open')) return;

      const moveUp = direction === 'up';
      const selectedElement = this.querySelector('[aria-selected="true"]');

      // Filter out hidden elements (duplicated page and article resources) thanks
      // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
      const allVisibleElements = Array.from(this.querySelectorAll('[role="option"], button.predictive-search__item')).filter(
          (element) => element.offsetParent !== null
      );
      if (!allVisibleElements.length) return;
      let activeElementIndex = 0;

      if (moveUp && !selectedElement) return;

      let selectedElementIndex = -1;
      let i = 0;

      while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
        if (allVisibleElements[i] === selectedElement) {
          selectedElementIndex = i;
        }
        i++;
      }

      this.statusElement.textContent = '';

      if (!moveUp && selectedElement) {
        activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
      }

      if (activeElementIndex === selectedElementIndex) return;

      const activeElement = allVisibleElements[activeElementIndex];

      activeElement.setAttribute('aria-selected', true);
      const panelRect = this.predictiveSearchResults.getBoundingClientRect();
      const optionRect = activeElement.getBoundingClientRect();
      if (optionRect.top < panelRect.top) {
        this.predictiveSearchResults.scrollTop -= panelRect.top - optionRect.top;
      } else if (optionRect.bottom > panelRect.bottom - 56) {
        this.predictiveSearchResults.scrollTop += optionRect.bottom - panelRect.bottom + 56;
      }
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);

      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }

    selectOption() {
      const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

      if (selectedOption) selectedOption.click();
    }

    getSearchResults(searchTerm) {
      this.abortController.abort();
      this.abortController = new AbortController();
      const requestId = this.requestId = (this.requestId || 0) + 1;
      const formData = new FormData(this.input.form);
      const params = new URLSearchParams(formData);
      const configForm = this.previousElementSibling;
      if (configForm?.matches('form.predictive_search_form')) {
        new FormData(configForm).forEach((value, key) => params.set(key, value));
      }

      if (this.productTypes && this.productTypes.value.length > 0) {
        const typeValue = this.productTypes.value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        searchTerm = `product_type:"${typeValue}" AND ${searchTerm}`;
      }

      params.set('q', searchTerm);
      params.delete('type');
      params.set('resources[type]', 'product');
      params.set('resources[limit]', '5');
      params.set('resources[options][unavailable_products]', 'last');
      params.set('section_id', 'predictive-search');

      const urlParams = params.toString();
      const queryKey = urlParams.toLowerCase();

      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      fetch(`${routes.predictive_search_url}?${urlParams}`, {
        signal: this.abortController.signal,
      })
          .then((response) => {
            if (!response.ok) {
              var error = new Error(response.status);
              this.close();
              throw error;
            }

            return response.text();
          })
          .then((text) => {
            if (requestId !== this.requestId || this.getQuery() !== this.searchTerm) return;
            const resultsMarkup = new DOMParser()
                .parseFromString(text, 'text/html')
                .querySelector('#shopify-section-predictive-search').innerHTML;
            // Save bandwidth keeping the cache in all instances synced
            this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
              predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
            });
            this.renderSearchResults(resultsMarkup);
          })
          .catch((error) => {
            if (error?.name === 'AbortError' || error?.code === 20 || requestId !== this.requestId) {
              // Code 20 means the call was aborted
              return;
            }
            this.close();
            throw error;
          });
    }

    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
      this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', true);
    }

    setLiveRegionText(statusText) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    renderSearchResults(resultsMarkup) {
      this.querySelector('.search-suggestions')?.classList.add('hidden');

      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.predictiveSearchResults.scrollTop = 0;
      const listbox = this.predictiveSearchResults.querySelector('[role="listbox"]');
      if (listbox && this.input.id) {
        listbox.id = `${this.input.id}-listbox`;
        this.input.setAttribute('aria-controls', listbox.id);
        this.input.setAttribute('aria-owns', listbox.id);
        listbox.querySelectorAll('[role="option"]').forEach((option, index) => {
          option.id = `${this.input.id}-option-${index}`;
        });
      }
      const viewAll = this.predictiveSearchResults.querySelector('.phi-search-all');
      if (viewAll) {
        const url = new URL(viewAll.href, window.location.origin);
        url.searchParams.set('q', this.getQuery());
        if (this.productTypes?.value) url.searchParams.set('filter.p.product_type', this.productTypes.value);
        viewAll.href = url.toString();
        viewAll.textContent = `Ver todos los resultados para “${this.getQuery()}”`;
      }
      this.setAttribute('results', true);

      this.setLiveRegionResults();
      this.open();
      if (window.updateViewportSize) window.updateViewportSize();
      requestAnimationFrame(() => this.updateResultsHeight());
    }

    setLiveRegionResults() {
      this.removeAttribute('loading');
      this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
    }

    showDiscovery() {
      if (!this.querySelector('[data-phi-initial]')) return;
      this.open();
      this.removeAttribute('open');
      this.removeAttribute('results');
      this.setAttribute('initial', '');
      this.input.setAttribute('aria-expanded', 'false');
      requestAnimationFrame(() => this.updateResultsHeight());
    }

    updateResultsHeight() {
      if (!this.isOpen) return;
      const viewport = window.visualViewport;
      const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
      const panel = this.hasAttribute('initial') ? this.querySelector('[data-phi-initial]') : this.predictiveSearchResults;
      this.style.setProperty('--phi-search-clearance', '0px');
      const header = this.closest('.header__search-bar')?.closest('.section-header');
      if (header && getComputedStyle(panel).position === 'absolute') {
        const clearance = Math.max(0, header.getBoundingClientRect().bottom + 8 - panel.getBoundingClientRect().top);
        this.style.setProperty('--phi-search-clearance', `${clearance}px`);
      }
      const top = panel.getBoundingClientRect().top;
      const available = Math.max(0, Math.min(560, bottom - top - 12));
      this.style.setProperty('--phi-search-height', `${available}px`);
    }

    open() {
      this.removeAttribute('initial');
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
      this.viewportHandler = this.viewportHandler || this.updateResultsHeight.bind(this);
      window.addEventListener('resize', this.viewportHandler);
      window.visualViewport?.addEventListener('resize', this.viewportHandler);
      window.visualViewport?.addEventListener('scroll', this.viewportHandler);
      this.outsideHandler = this.outsideHandler || ((event) => {
        if (!this.contains(event.target)) this.close();
      });
      document.addEventListener('pointerdown', this.outsideHandler);
      requestAnimationFrame(this.viewportHandler);
    }

    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm = false) {
      this.removeAttribute('initial');
      this.abortController.abort();
      this.requestId = (this.requestId || 0) + 1;
      window.removeEventListener('resize', this.viewportHandler);
      window.visualViewport?.removeEventListener('resize', this.viewportHandler);
      window.visualViewport?.removeEventListener('scroll', this.viewportHandler);
      document.removeEventListener('pointerdown', this.outsideHandler);
      this.isOpen = false;
      if (clearSearchTerm) {
        this.input.value = '';
      }
      this.querySelector('.search-suggestions')?.classList.remove('hidden');
      const selected = this.querySelector('[aria-selected="true"]');

      if (selected) selected.setAttribute('aria-selected', false);

      this.input.setAttribute('aria-activedescendant', '');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.removeAttribute('results');
      this.input.setAttribute('aria-expanded', false);
      this.predictiveSearchResults.removeAttribute('style');
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
});
